<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import * as Sentry from '@sentry/vue';
import useStatusFeedback, { type StatusFeedback } from '~/models/StatusFeedback';
import useToast from '~/lib/hooks/toast';

interface Props {
    statusAnnouncementUuid: string;
}

interface Emits {
    (event: 'created', statusFeedback: StatusFeedback): void;
    (event: 'cancelled'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const validationSchema = toTypedSchema(z.object({
    feedback: z.string(),
}));
const form = useForm({ validationSchema });

const handleSubmit = form.handleSubmit(async values => {
    try {
        const response = await useStatusFeedback(props.statusAnnouncementUuid).create({
            feedback: values.feedback,
            sentry_id: Sentry.getCurrentScope().getScopeData().span?.spanContext().traceId,
        });

        useToast({
            type: 'success',
            header: 'status.feedback.form.messages.submit_success',
            context: {},
        });

        emit('created', response);
    } catch {
        useToast({
            type: 'error',
            header: 'status.feedback.form.messages.submit_error',
            context: {},
        });
    }
});
</script>

<template>
    <form
        class="grid grid-cols-2 gap-4 w-full"
        @submit.prevent="handleSubmit"
    >
        <Textarea
            name="feedback"
            :label="$t('status.feedback.form.labels.feedback')"
            rows="4"
            wrapper-classes="col-span-2 w-full"
        />

        <Button
            type="submit"
            class="col-span-2"
        >
            {{ $t('status.feedback.form.actions.submit') }}
        </Button>

        <Button
            class="col-span-2"
            theme="outline"
            @click="$emit('cancelled')"
        >
            {{ $t('status.feedback.form.actions.cancel') }}
        </Button>
    </form>
</template>
